import * as angular from "angular";

import template from "./timezone-select.html";
import "./timezone-select.less";
import {
	TIME_ZONE_FUSE_OPTIONS,
	TimeZoneChoice,
	TimeZoneSelectItem,
	buildTimeZoneChoice,
	buildTimeZoneSelectItems,
	getSelectedTimeZoneDisplay
} from "./timezoneSelectModel";

export interface TimeZoneSelectProps {
	id?: string;
}

export interface TimeZoneSelectValue {
	Code: string;
}

export interface TimeZoneChoicesBox {
	clearSelected(): void;
	setChoices(
		choices: readonly TimeZoneChoice[],
		valueProperty: string,
		labelProperty: string,
		replaceChoices: boolean
	): unknown;
	setValueByChoice(value: string): unknown;
}

export interface TimeZoneSelectController {
	isDisabled?: boolean;
	selectProps?: TimeZoneSelectProps;
	label?: string;
	ngModel: angular.INgModelController;
}

// AngularJS owns these merged binding and required-controller properties.
// eslint-disable-next-line no-redeclare
export class TimeZoneSelectController {
	public static $inject = ["$scope"];

	public readonly timeZones: readonly TimeZoneSelectItem[] = buildTimeZoneSelectItems();
	public readonly choices: readonly TimeZoneChoice[] = this.timeZones.map(buildTimeZoneChoice);
	public readonly fuseOptions = TIME_ZONE_FUSE_OPTIONS;
	public selectedCode = "";

	private readonly generatedSelectId: string;
	private choicesBox?: TimeZoneChoicesBox;

	public constructor(private readonly $scope: angular.IScope) {
		this.generatedSelectId = `timezone-select:${this.$scope.$id}`;
	}

	public get selectId(): string {
		return this.selectProps?.id || this.generatedSelectId;
	}

	public $onInit(): void {
		this.ngModel.$isEmpty = value => !value?.Code;
		this.ngModel.$render = () => this.renderModelValue();
	}

	public setTimeZoneBox(box: TimeZoneChoicesBox): void {
		this.choicesBox = box;
		box.setChoices(this.choices, "value", "label", true);
		this.renderModelValue();
	}

	public getSelectedDisplay(value: string | undefined): string {
		return getSelectedTimeZoneDisplay(value, this.choices);
	}

	public handleValueChanged(value: string | undefined): void {
		const selectedCode = value ?? "";
		this.selectedCode = selectedCode;

		this.$scope.$evalAsync(() => {
			if((this.ngModel.$viewValue?.Code ?? "") !== selectedCode) {
				const modelValue: TimeZoneSelectValue | null = selectedCode
					? { Code: selectedCode }
					: null;
				this.ngModel.$setViewValue(modelValue);
			}
		});
	}

	private renderModelValue(): void {
		this.selectedCode = this.ngModel.$viewValue?.Code ?? "";

		if(!this.choicesBox) {
			return;
		}

		this.choicesBox.clearSelected();
		if(this.selectedCode) {
			this.choicesBox.setValueByChoice(this.selectedCode);
		}
	}
}

export const timeZoneSelectComponent: angular.IComponentOptions = {
	template,
	controller: TimeZoneSelectController,
	bindings: {
		isDisabled: "<?",
		selectProps: "<?",
		label: "@?"
	},
	require: {
		ngModel: "ngModel"
	}
};

export default angular
	.module("vesl.components.timezone-select", [])
	.component("timezoneSelect", timeZoneSelectComponent)
	.name;
